import type { TimeRange } from '@mimir/common/util/timeRange';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import type { ExtendedItemDto } from '@mimir/common/dto/extendedItemDto';
import type { DownloadPlan } from '../fileSync/downloadIdentity';
import { getPremiereSubclipTimeRangeInSeconds } from '../util/premiere';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { getItemSystemDefaultField } from '@mimir/common/mdf/MetaDataForm';
import type { PremiereSubclip } from '../model/ongoingDownload';
import { getStartTimeSeconds } from '../model/playableItem';
import type { Logger } from '@mimir/common/types/logger';

export type SubclipOriginItem = Pick<ExtendedItemDto, 'id' | 'metadata' | 'timecode' | 'mediaFrameRate' | 'technicalMetadata'>;

export function getSubclipToImport(
  logger: Logger,
  plan: DownloadPlan,
  isSubclip: boolean,
  timeRange: TimeRange | null,
  virtualClip: VirtualClipModel | null,
  title: string,
  originItem: SubclipOriginItem
): PremiereSubclip | null {
  const deliver = plan.deliver;
  if (deliver.type == 'DeliverSubclip') {
    // A segment range is handled by the 'DeliverSubclip' delivery type, but
    // if it actually covers the whole media, we don't want to create a
    // subclip object for it, since that would mean a double import
    if ((virtualClip === null) && !isSubclip || (deliver.whole === true)) {
      return null;
    }

    const range = deliver.artifactRange;
    return {
      title,
      startTime: range.start.approximate,
      endTime: range.end.approximate,
      id: originItem.id,
      premiereStartTimeSeconds: getStartTimeSeconds(logger, originItem, timeRange?.start ? getPremiereSubclipTimeRangeInSeconds(timeRange.start) : 0),
    };
  }

  if (plan.dlId.artifactReference.type != 'ImplicitReference') {
    return null;
  }

  if (isSubclip) {
    if (!timeRange) {
      return null;
    }
    const offset = virtualClip?.inPoint ?? 0;
    const startTime = getPremiereSubclipTimeRangeInSeconds(timeRange.start, offset);
    return {
      title,
      startTime,
      endTime: getPremiereSubclipTimeRangeInSeconds(timeRange.end, offset),
      id: originItem.id,
      premiereStartTimeSeconds: getStartTimeSeconds(logger, originItem, startTime),
    };
  }

  if (!virtualClip) {
    return null;
  }

  const startTime = virtualClip.inPoint / 1000;
  const endTime = virtualClip.outPoint / 1000;
  return {
    startTime,
    endTime,
    title: getItemSystemDefaultField(originItem, SystemDefaultField.Title),
    id: originItem.id,
    premiereStartTimeSeconds: getStartTimeSeconds(logger, originItem, startTime),
  };
}
